// import type { ZodTypeAny } from 'zod';
import type { NotificationFormAnonymousProps } from '../../../site/parts/notificationFormAnonymous/notificationFormAnonymous.d';
import type { BodyObj } from '../../../services/formInput/formInput.d';
import type { ReCaptchaData } from '../../../site/parts/notificationFormAnonymous/ReCaptchaData.d';

import {
	type ColorName,
	type PillShape,
	// type Substance,
	type SubstanceForm,
	COLOR_VALUES,
	NOTIFICATION_TYPE,
	PILL_SHAPE_VALUES,
	SHARE_POINT,
	SUBSTANCE_FORM_VALUES,
	// SUBSTANCE_VALUES,
	ZOD_TOO_BIG_ISSUE_TYPE,
} from '../../../react4xp/overdosefare/notificationForm/common/constants';
import {
	// type When,
	type AnonymousWhere,
	ANONYMOUS_FIELD_NAME,
	WHEN_WAS_THE_DRUG_TAKEN_VALUES,
} from '../../../react4xp/overdosefare/notificationForm/anonymous/constants';

import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { TooltipProvider } from '@hdir/ui/tooltip';
import i18next from 'i18next';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { z, ZodIssueCode } from 'zod';
import { zodI18nMap } from 'zod-i18n-map';
import translation from 'zod-i18n-map/locales/nb/zod.json';
// import { zodToJsonSchema } from 'zod-to-json-schema';

import { Alert } from '/react4xp/components/alert/Alert';
import { Form } from '/react4xp/components/react-hook-form/Form';
import { ErrorSummary } from '/react4xp/components/react-hook-form/ErrorSummary';
import { type ReCAPTCHAType, AccessibleReCAPTCHA } from '/react4xp/components/AccessibleReCAPTCHA';
import { SubmitButton } from '/react4xp/components/react-hook-form/SubmitButton';
import { Stack, Row } from '/react4xp/components/Flex';
// import { Json } from '/react4xp/components/Json';
import { sanitizeHtml } from '/react4xp/shared/sanitizeHtml';
import {
	ACCEPT_APPLICATION_JSON,
	CONTENT_TYPE_APPLICATION_JSON_UTF8,
} from '../../../services/formInput/constants';
import { Span, cn } from '/react4xp/components/Element';
import { FontAwesomeIconWrapper } from '/react4xp/components/FontAwesomeIconWrapper';

// Common
import { convertFileToBase64 } from '../../../react4xp/overdosefare/notificationForm/common/util/convertFileToBase64';
import { AdditionalInformationTextArea } from '../../../react4xp/overdosefare/notificationForm/common/AdditionalInformationTextArea';
import { WhatHappensAccordion } from '../../../react4xp/overdosefare/notificationForm/common/WhatHappensAccordion';
import { ColorFieldset } from '../../../react4xp/overdosefare/notificationForm/common/color/ColorFieldset';
import { getTranslatedColors } from '../../../react4xp/overdosefare/notificationForm/common/color/getTranslatedColors';
import { CapsulePowderColorFieldset } from '../../../react4xp/overdosefare/notificationForm/common/capsulePowderColor/CapsulePowderColorFieldset';
import { PillShapeFieldset } from '../../../react4xp/overdosefare/notificationForm/common/pillShape/PillShapeFieldset';
import { SubstanceFieldset } from '../../../react4xp/overdosefare/notificationForm/common/substance/SubstanceFieldset';
import { getTranslatedColorButtonGroupOptions } from '../../../react4xp/overdosefare/notificationForm/common/color/getTranslatedColorButtonGroupOptions';
import { SubstanceFormButtonGroup } from '../../../react4xp/overdosefare/notificationForm/common/SubstanceFormButtonGroup';
import { IntoxicantDescriptionTextField } from '../../../react4xp/overdosefare/notificationForm/common/IntoxicantDescriptionTextField';
import {
	// SIXTY_MB_IN_BYTES,
	ImageUpload,
} from '../../../react4xp/overdosefare/notificationForm/common/ImageUpload';
import { CancelDialog } from '../../../react4xp/overdosefare/notificationForm/common/CancelDialog';

// Anonymous specific
import { WhenFieldset } from '../../../react4xp/overdosefare/notificationForm/anonymous/when/WhenFieldset';
import { WhereFieldset } from '../../../react4xp/overdosefare/notificationForm/anonymous/where/WhereFieldset';
import { SuccessPage } from '../../../react4xp/overdosefare/notificationForm/anonymous/SuccessPage';

import { StateIndicator } from '../../../react4xp/components/react-hook-form/StateIndicator';
import { Heading } from '../../../react4xp/components/heading/Heading';
import { P } from '../../../react4xp/components/Paragraph';

import { notScriptTagOrDangerousChar, localizedScriptTagOrDangerousCharError } from './refineNotScript';


export type { NotificationFormAnonymousProps } from '../../../site/parts/notificationFormAnonymous/notificationFormAnonymous.d';


const TRACE = false;


// translation.errors.invalid_enum_value = 'Velg et alternativ';
// translation.errors.too_small.array.inclusive = 'Velg minst {{minimum}} alternativ';

i18next.init({
	lng: 'nb',
	resources: {
		nb: { zod: translation },
	},
})
	.then(() => {
		// no-op
	})
	.catch((error) => console.error('i18next initialization failed:', error));

// Create a custom error map that extends zodI18nMap
const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
	const {
		code,
		// exact,
		// message,
		path,
	} = issue;

	if (code === ZodIssueCode.too_big) {
		const {
			inclusive,
			maximum,
			type,
		} = issue;
		if (type === ZOD_TOO_BIG_ISSUE_TYPE.STRING && inclusive) {
			return {
				message: `Det er maks tillatt med ${maximum} tegn. Vennligst kort ned teksten.`,
			};
		}
	}

	if (path[0] === ANONYMOUS_FIELD_NAME.SUBSTANCE) {
		return {
			message: 'Velg et rusmiddel eller klikk annet.',
		};
	}

	if (path[0] === ANONYMOUS_FIELD_NAME.WHERE) {
		return {
			message: 'Velg en kommune eller klikk annet.',
		};
	}

	if (path[0] === ANONYMOUS_FIELD_NAME.WHEN_WAS_THE_DRUG_TAKEN) {
		return {
			message: 'Velg når rusmiddelet ble tatt eller klikk annet.',
		};
	}

	if (!issue.path.length)	return zodI18nMap(issue, ctx);

	if (TRACE) console.debug('TODO: Mangler tilpasset feilmelding på flere språk!', issue);

	return zodI18nMap(issue, ctx);
};

z.setErrorMap(customErrorMap);


const isoStringSchema = z.string().refine(
	(val) => {
		try {
			// Check if it can be parsed as a valid Date and matches ISO format
			const date = new Date(val);
			return date.toISOString() === val && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(val);
		} catch {
			return false;
		}
	},
	{
		message: "Must be a valid ISO 8601 string",
	},
);

const fileSchema = z.custom<File>((val) => {
	return val instanceof File;
}, {
	message: 'Must be a valid File object',
});

export function NotificationFormAnonymous(props: NotificationFormAnonymousProps): JSX.Element {
	const { data } = props;
	const {
		counties,
		intoxicantOptions,
		municipalityOptions,
		phrases,
		reCaptchaServiceUrl,
		reCaptchaSiteKey,
		serviceUrl,
		sitePageUrl,
	} = data;
	const recaptchaRef = createRef<ReCAPTCHAType>();

	const zodSchema = useMemo(() => {
		return z.object({
			[ANONYMOUS_FIELD_NAME.TYPE]: z.literal(NOTIFICATION_TYPE.ANONYMOUS),
			[ANONYMOUS_FIELD_NAME.SUBSTANCE]: z.string(), // Comes from content

			// ERROR: Type system thinks this is optional
			// [ANONYMOUS_FIELD_NAME.SUBSTANCE]: z.union(SUBSTANCE_VALUES.map((v) => z.literal(v)) as unknown as readonly [ZodTypeAny, ZodTypeAny, ...ZodTypeAny[]]),

			[ANONYMOUS_FIELD_NAME.SUBSTANCE_DETAILS]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.SUBSTANCE_OTHER]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.WHERE]: z.string().min(1),

			[ANONYMOUS_FIELD_NAME.WHERE_OTHER]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.MUNICIPALITY_NAME]: z.string().optional(),
			[ANONYMOUS_FIELD_NAME.COUNTY_CODE]: z.string().optional(),
			[ANONYMOUS_FIELD_NAME.COUNTY_DESCRIPTION]: z.string().optional(),

			[ANONYMOUS_FIELD_NAME.WHEN_WAS_THE_DRUG_TAKEN]: z.enum(WHEN_WAS_THE_DRUG_TAKEN_VALUES),

			[ANONYMOUS_FIELD_NAME.WHEN_WAS_THE_DRUG_TAKEN_OTHER]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.SUBSTANCE_FORM]: z.enum(SUBSTANCE_FORM_VALUES).optional(),

			[ANONYMOUS_FIELD_NAME.PILL_SHAPE]: z.enum(PILL_SHAPE_VALUES).optional(),

			[ANONYMOUS_FIELD_NAME.PILL_SHAPE_OTHER]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.COLOR]: z.array(z.enum(COLOR_VALUES)).optional(),

			[ANONYMOUS_FIELD_NAME.COLOR_OTHER]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.CAPSULE_POWDER_COLOR]: z.array(z.enum(COLOR_VALUES)).optional(),

			[ANONYMOUS_FIELD_NAME.CAPSULE_POWDER_COLOR_OTHER]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.INTOXICANT_DESCRIPTION]: z
				.string()
				.max(SHARE_POINT.INPUT_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			// NOTE: This is only here so types are correct under onSubmit
			[ANONYMOUS_FIELD_NAME.IMAGES]: z.array(fileSchema).optional(),
			// [ANONYMOUS_FIELD_NAME.IMAGES]: z.array(z.string().base64()).optional(),

			// This is how the JSON schema looks frontend:
			// [ANONYMOUS_FIELD_NAME.IMAGES]: z.array(z.instanceof(File))
			// 	.min(1)
			// 	.max(3)
			// 	.optional()
			// 	.refine((files) => files ? files.every(file => file.size <= SIXTY_MB_IN_BYTES) : true, "File size must be less than 60 MB")
			// 	.refine((files) => files ? files.every(file => ['image/jpeg', 'image/png'].includes(file.type)): true, "File type must be jpeg or png"),

			[ANONYMOUS_FIELD_NAME.ADDITIONAL_INFORMATION]: z
				.string()
				.max(SHARE_POINT.TEXT_AREA_MAX_LENGTH)
				.refine(
					(str) => notScriptTagOrDangerousChar(str),
					(str) => localizedScriptTagOrDangerousCharError({ locale: 'nb', str }),
				)
				.optional(),

			[ANONYMOUS_FIELD_NAME.SUBMISSION_DATE]: isoStringSchema,
		});
	}, [/* whereOptions */]);

	// const jsonSchema7 = useMemo(() => zodToJsonSchema(zodSchema, {
	// 	target: 'jsonSchema7',
	// }), [zodSchema]);

	const [formData, setData] = useState<z.infer<typeof zodSchema> & {
		imageDataUrls?: string[];
	}>();
	const [doingSend, setDoingSend] = useState(false);
	const [isSent, setIsSent] = useState(false);
	const [isSentSuccessful, setIsSentSuccessful] = useState(false);

	const [doingReCaptchaGet, setDoingReCaptchaGet] = useState(false);
	const [doingReCaptchaVerify, setDoingReCaptchaVerify] = useState(false);
	const [isReCaptchaVerified, setIsReCaptchaVerified] = useState<boolean|undefined>(undefined);

	const translatedColors = getTranslatedColors({ phrases });

	const {
		capsuleContainerColorOptions,
		capsulePowderColorOptions,
		crystalColorOptions,
		liquidColorOptions,
		pillColorOptions,
		powderColorOptions,
	} = getTranslatedColorButtonGroupOptions({
		phrases,
	});

	useEffect(() => {
		async function doChallenge(): Promise<void> {
			try {
				setDoingReCaptchaGet(true);
				const responseToken = await recaptchaRef.current?.executeAsync();
				if (!responseToken) {
					throw new Error('reCAPTCHA token is undefined');
				}
				setDoingReCaptchaGet(false);
				setDoingReCaptchaVerify(true);
				const response = await fetch(reCaptchaServiceUrl, {
					body: JSON.stringify({
						responseToken,
					}),
					headers: {
						'Accept': ACCEPT_APPLICATION_JSON,
						'Content-Type': CONTENT_TYPE_APPLICATION_JSON_UTF8,
					},
					method: 'POST',
				});
				if (!response.ok) {
					throw new Error(`Fetch failed with status ${response.status}`);
				}
				const data = await response.json() as ReCaptchaData;
				setDoingReCaptchaVerify(false);
				setIsReCaptchaVerified(!!data?.success);
			} catch (error) {
				if (error) { // Ignore when error is Falsy
					console.error('Error in doChallenge:', error);
				}
			}
		}
		void doChallenge();
	}, [
		reCaptchaServiceUrl,
	]);

	if (isSentSuccessful && formData) return (
		<SuccessPage
			additionalInformation={formData[ANONYMOUS_FIELD_NAME.ADDITIONAL_INFORMATION]}
			capsulePowderColors={formData[ANONYMOUS_FIELD_NAME.CAPSULE_POWDER_COLOR] as ColorName[]}
			capsulePowderColorOther={formData[ANONYMOUS_FIELD_NAME.CAPSULE_POWDER_COLOR_OTHER]}
			colors={formData[ANONYMOUS_FIELD_NAME.COLOR] as ColorName[]}
			colorOther={formData[ANONYMOUS_FIELD_NAME.COLOR_OTHER]}
			imageDataUrls={formData['imageDataUrls']}
			intoxicantDescription={formData[ANONYMOUS_FIELD_NAME.INTOXICANT_DESCRIPTION]}
			intoxicantOptions={intoxicantOptions}
			municipalityName={formData[ANONYMOUS_FIELD_NAME.MUNICIPALITY_NAME]}
			phrases={phrases}
			pillShape={formData[ANONYMOUS_FIELD_NAME.PILL_SHAPE] as PillShape}
			pillShapeOther={formData[ANONYMOUS_FIELD_NAME.PILL_SHAPE_OTHER]}
			sitePageUrl={sitePageUrl}
			submissionDate={formData[ANONYMOUS_FIELD_NAME.SUBMISSION_DATE]}
			substance={formData[ANONYMOUS_FIELD_NAME.SUBSTANCE]}
			substanceForm={formData[ANONYMOUS_FIELD_NAME.SUBSTANCE_FORM] as SubstanceForm}
			substanceOther={formData[ANONYMOUS_FIELD_NAME.SUBSTANCE_DETAILS] || formData[ANONYMOUS_FIELD_NAME.SUBSTANCE_OTHER]}
			translatedColors={translatedColors}
			where={formData[ANONYMOUS_FIELD_NAME.WHERE] as AnonymousWhere}
			whereOther={formData[ANONYMOUS_FIELD_NAME.WHERE_OTHER]}
			whenWasTheDrugTaken={formData[ANONYMOUS_FIELD_NAME.WHEN_WAS_THE_DRUG_TAKEN]}
			whenWasTheDrugTakenOther={formData[ANONYMOUS_FIELD_NAME.WHEN_WAS_THE_DRUG_TAKEN_OTHER]}
		/>
	);

	return (
		<TooltipProvider>
			<Stack className='gap-lg' padding={{ block: 'lg' }}>
				<Stack className='hidden tablet:flex'>
					<Heading as='h1' dangerouslySetInnerHTML={{ __html: sanitizeHtml(phrases['notificationForm.anonymous.heading']) }} textSize='3xl'/>
					<Span>
						<FontAwesomeIconWrapper className={cn({color: 'iconFeedbackInfoDefault'})} icon={faCircleInfo}/>
						&nbsp;
						{phrases['notificationForm.term.doNotEnterPersonalInformation']}
					</Span>
				</Stack>
				<Alert variant='warning'>{phrases['notificationForm.anonymous.emergencyAlert.text']}</Alert>
				<Form
					onSubmit={async (fieldValues: z.infer<typeof zodSchema>) => {
						// This deconstruction works because ANONYMOUS_FIELD_NAME.IMAGES === imagesTmp.
						const {imagesTmp, ...convertedFieldValues} = fieldValues;
						let images: string[] = [];
						if (imagesTmp) {
							images = await Promise.all(
								imagesTmp.map(
									async (file: File) => {
										const base64File: string = await convertFileToBase64(file);
										return base64File;
									},
								),
							);
						}
						const municipalities: Record<string, string> = {};
						for (const municipalityOption of municipalityOptions) {
							const {
								id,
								label,
							} = municipalityOption;
							municipalities[id] = label;
						}
						const municipalityName = municipalities[convertedFieldValues[ANONYMOUS_FIELD_NAME.WHERE]];
						let countyCode = '99';
						let countDescription = counties[countyCode];

						if (municipalityName) {
							const municipalityCode = convertedFieldValues[ANONYMOUS_FIELD_NAME.WHERE];
							countyCode = municipalityCode.substring(0, 2);
							countDescription = counties[countyCode];
							if (!countDescription) {
								countDescription = `Fant ikke fylke fra kommunenr ${municipalityCode}`;
							}
							convertedFieldValues[ANONYMOUS_FIELD_NAME.MUNICIPALITY_NAME] = municipalityName;
						}
						convertedFieldValues[ANONYMOUS_FIELD_NAME.COUNTY_CODE] = countyCode;
						convertedFieldValues[ANONYMOUS_FIELD_NAME.COUNTY_DESCRIPTION] = countDescription;

						convertedFieldValues[ANONYMOUS_FIELD_NAME.SUBMISSION_DATE] = new Date().toISOString();
						setData({
							...convertedFieldValues,
							imageDataUrls: images,
						});
						setDoingSend(true);
						const bodyObj: BodyObj = {
							form: convertedFieldValues,
							images,
						};
						fetch(serviceUrl, {
							body: JSON.stringify(bodyObj),
							headers: {
								'Accept': ACCEPT_APPLICATION_JSON,
								'Content-Type': CONTENT_TYPE_APPLICATION_JSON_UTF8,
							},
							method: 'POST',
						})
							.then(response => response.json())
							.then(() => {
								setDoingSend(false);
								setIsSent(true);
								setIsSentSuccessful(true);
							})
							.catch((error) => console.error('fetch failed:', error));
					}}

					useFormProps={{
						defaultValues: {
							[ANONYMOUS_FIELD_NAME.TYPE]: NOTIFICATION_TYPE.ANONYMOUS,
							[ANONYMOUS_FIELD_NAME.SUBMISSION_DATE]: new Date().toISOString(), // Replaced in onSubmit
						},
					}}

					zodSchema={zodSchema}
				>
					<Stack className='gap-lg tablet:gap-xl'>
						<Stack rounded className='bg-surface-neutral-default gap-lg tablet:gap-xl'>
							<Stack className='tablet:hidden desktop:hidden'>
								<Heading as='h1' dangerouslySetInnerHTML={{ __html: sanitizeHtml(phrases['notificationForm.anonymous.heading']) }} textSize='xl'/>
								<Span>
									<FontAwesomeIconWrapper className={cn({color: 'iconFeedbackInfoDefault'})} icon={faCircleInfo}/>
									&nbsp;
									{phrases['notificationForm.term.doNotEnterPersonalInformation']}
								</Span>
							</Stack>
							<P
								dangerouslySetInnerHTML={{ __html: sanitizeHtml(
									phrases['notificationForm.common.helpText'],
									{
										whiteList: {
											span: ['class'],
										},
									},
								)}}
							/>

							<SubstanceFieldset
								intoxicantOptions={intoxicantOptions}
								phrases={phrases}
							/>
							<WhereFieldset
								label={phrases['notificationForm.anonymous.whereField.label']}
								municipalityOptions={municipalityOptions}
								phrases={phrases}
							/>
							<WhenFieldset
								phrases={phrases}
							/>

							<SubstanceFormButtonGroup
								phrases={phrases}
							/>
							<PillShapeFieldset
								phrases={phrases}
							/>

							<ColorFieldset
								capsuleContainerColorOptions={capsuleContainerColorOptions}
								crystalColorOptions={crystalColorOptions}
								liquidColorOptions={liquidColorOptions}
								phrases={phrases}
								pillColorOptions={pillColorOptions}
								powderColorOptions={powderColorOptions}
							/>
							<CapsulePowderColorFieldset
								capsulePowderColorOptions={capsulePowderColorOptions}
								phrases={phrases}
							/>
							<IntoxicantDescriptionTextField
								phrases={phrases}
							/>

							<ImageUpload phrases={phrases}/>
							<AdditionalInformationTextArea phrases={phrases}/>

						</Stack>

						<WhatHappensAccordion phrases={phrases}/>

						{
							reCaptchaSiteKey
								? <AccessibleReCAPTCHA
									badge='inline'
									ref={recaptchaRef}
									sitekey={reCaptchaSiteKey}
									size='invisible'
								/> : null
						}

						<ErrorSummary title={phrases['notificationForm.common.errorSummary.title']} />

						<StateIndicator
							doingReCaptchaGet={doingReCaptchaGet}
							doingReCaptchaVerify={doingReCaptchaVerify}
							doingSend={doingSend}
							textWhenReCaptchaGet={phrases['notificationForm.common.submitButton.textWhen.reCaptchaGet']}
							textWhenReCaptchaVerify={phrases['notificationForm.common.submitButton.textWhen.reCaptchaVerify']}
							textWhenSending={phrases['notificationForm.common.submitButton.textWhen.sending']}
							textWhenValidating={phrases['notificationForm.common.submitButton.textWhen.validating']}
						/>
						<Row className='gap-sm justify-between'>
							<CancelDialog
								href={sitePageUrl}
								phrases={phrases}
							/>
							<SubmitButton
								isReCaptchaVerified={isReCaptchaVerified}
								isSent={isSent}
								isSentSuccessful={isSentSuccessful}
								text={phrases['notificationForm.common.submitButton.label']}
								textWhenSendSuccessful={phrases['notificationForm.common.submitButton.textWhen.sendSuccessful']}
							/>
						</Row>
					</Stack>
				</Form>
				{/* <Stack rounded bgColor='surfaceNeutralSubtle' flexDirection='col'>
					<h2>Data</h2>
					<Json value={formData} enableClipboard/>
				</Stack> /*}
				{/* <Stack rounded bgColor='surfaceNeutralSubtle' flexDirection='col'>
					<h2>JSON schema (generated from Zod schema)</h2>
					<Json value={jsonSchema7} collapsed enableClipboard/>
				</Stack> */}
			</Stack>
		</TooltipProvider>
	);
}
